import { LocalDate, nativeJs } from '@js-joda/core';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Checkbox,
	Input,
	Label,
	Paragraph,
	Select,
	Separator,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import BulkModal from './BulkModal';

const TableCell = styled(Box)`
	color: ${tokens.text.secondary};
`;

const TableHeader = styled(TableCell)`
	font-weight: bold;
	font-size: 12px;
`;

function CyclePreviewMoments(props: { cycleTemplateID: string; startDate: Date }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const { data, loading } = graphql.useCycleTemplateQuery({
		variables: {
			id: props.cycleTemplateID,
		},
	});

	if (loading) {
		return (
			<Flex justifyContent="center" flexGrow={1} mt={4}>
				<Spinner />
			</Flex>
		);
	}

	return (
		<>
			<Box mt={3}>
				<Flex mb={3} alignItems="center">
					<TableHeader flexGrow={1}>{t('employees.bulkAssignCycle.preview.moment')}</TableHeader>
					<TableHeader width={100}>{t('employees.bulkAssignCycle.preview.startDate')}</TableHeader>
					<TableHeader width={100}>{t('employees.bulkAssignCycle.preview.endDate')}</TableHeader>
				</Flex>
				<Separator />
			</Box>
			{data?.cycleTemplate.moments.map((momentTemplate, index) => (
				<Box key={index}>
					<Flex alignItems="center" paddingY={10}>
						<Flex flex={1} alignItems="center">
							<TableCell marginRight={12}>{localize(momentTemplate.title).text}</TableCell>
						</Flex>
						<TableCell width={100}>
							{moment(props.startDate).add(momentTemplate.startOffset, 'days').format('L')}
						</TableCell>
						<TableCell width={100}>
							{moment(props.startDate)
								.add(momentTemplate.startOffset + momentTemplate.duration, 'days')
								.format('L')}
						</TableCell>
					</Flex>
					<Separator />
				</Box>
			))}
		</>
	);
}

/**
 * Calculates the cycle start date based on the user's activated at date and the cycle duration in months.
 * We adjust the activated at date to the current year (2025) and then add or subtract the cycle duration in months.
 * This makes sure that the cycle start date matches the expected date, which is the activated at date.
 */
function getCycleStartDateBasedOnActivatedAt(user: graphql.User, cycleDurationInMonths: number) {
	const today = moment();
	const adjustedActivatedAt = moment(user.activatedAt).set('year', 2025);

	if (adjustedActivatedAt.isAfter(today)) {
		adjustedActivatedAt.subtract(1, 'year');
	}

	const cycleStartDate = adjustedActivatedAt.format('YYYY-MM-DD');

	return cycleStartDate;
}

export default function BulkAssignCycleModal(props: {
	users: graphql.User[];
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const cycles = graphql.useCycleTemplatesQuery({
		variables: {
			isArchived: false,
			first: 100,
		},
	});

	const [createCycleInstance] = graphql.useCreateCycleInstanceMutation();

	const cycleTemplates = cycles.data?.cycleTemplates.edges.map(({ node }) => ({
		title: localize(node.title).text,
		value: node.id,
		duration: node.duration,
	}));
	cycleTemplates?.sort((a, b) => a.title.localeCompare(b.title));

	const [cycleTemplateID, setCycleTemplateID] = useState<string | undefined>();
	const [startDate, setStartDate] = useState(new Date());
	const [planCycleBasedOnActivatedAt, setPlanCycleBasedOnActivatedAt] = useState(false);

	const assignCycleBasedOnActivatedAt = async (user: graphql.User) => {
		const selectedCycleTemplate = cycleTemplates?.find(
			(cycleTemplate) => cycleTemplate.value === cycleTemplateID,
		);

		if (!selectedCycleTemplate) {
			console.error('Cycle template not found.');
			return;
		}

		const cycleDurationInMonths = selectedCycleTemplate.duration;

		const cycleStartDate = getCycleStartDateBasedOnActivatedAt(user, cycleDurationInMonths);

		await createCycleInstance({
			variables: {
				input: {
					localStartDate: cycleStartDate,
					activatesAt: moment().startOf('day').toDate(),
					userID: user.id,
					cycleTemplateID: cycleTemplateID!,
				},
			},
		});
	};

	const assignCycleBasedOnCustomStartDate = async (user: graphql.User) => {
		const localStartDate = moment(startDate).format('YYYY-MM-DD');

		const activatesAt = moment
			.max(moment().startOf('day'), moment(startDate).startOf('day'))
			.toDate();

		await createCycleInstance({
			variables: {
				input: {
					localStartDate,
					activatesAt,
					userID: user.id,
					cycleTemplateID: cycleTemplateID!,
				},
			},
		});
	};

	const assignCycle = async (user: graphql.User) => {
		if (planCycleBasedOnActivatedAt) {
			return await assignCycleBasedOnActivatedAt(user);
		}

		return await assignCycleBasedOnCustomStartDate(user);
	};

	return (
		<BulkModal
			valid={!!cycleTemplateID}
			name="bulkAssignCycle"
			onDismiss={props.onDismiss}
			users={props.users}
			apply={assignCycle}
		>
			<Flex mb={3}>
				<Box mr={2} flexGrow={1} flexShrink={1} flexBasis={0}>
					<Label mb={2}>{t('employees.bulkAssignCycle.cycle.label')}</Label>
					{cycles.data ? (
						<Select
							placeholder={t('ui.select.placeholder')}
							options={cycleTemplates}
							selectedValue={cycleTemplateID}
							onChange={(value) => setCycleTemplateID(value)}
						/>
					) : (
						<Spinner />
					)}
				</Box>

				<Box ml={2} flexGrow={1} flexShrink={1} flexBasis={0}>
					<Label mb={2}>{t('employees.bulkAssignCycle.startDate.label')}</Label>
					<Input.DatePicker
						disabled={planCycleBasedOnActivatedAt}
						value={startDate}
						onChange={(startDate) => setStartDate(startDate ?? new Date())}
					/>
				</Box>
			</Flex>

			<Flex>
				<Box ml={2} flexGrow={1} flexShrink={1} flexBasis={0}>
					<Checkbox
						label={t('employees.bulkAssignCycle.planBasedOnActivatedAt.label')}
						checked={planCycleBasedOnActivatedAt}
						onClick={() => setPlanCycleBasedOnActivatedAt(!planCycleBasedOnActivatedAt)}
					/>
				</Box>
			</Flex>
			{cycleTemplateID && !planCycleBasedOnActivatedAt && (
				<Box my={3}>
					<Separator />
					<CyclePreviewMoments startDate={startDate} cycleTemplateID={cycleTemplateID} />
				</Box>
			)}

			{planCycleBasedOnActivatedAt && (
				<Box my={3}>
					<Separator />
					<Paragraph textAlign="center" my={3}>
						{t('employees.bulkAssignCycle.planBasedOnActivatedAt.description')}
					</Paragraph>
				</Box>
			)}
		</BulkModal>
	);
}
